import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ONBOARDING_LIMITS, type OnboardingLandingSettings } from 'shared'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '../ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

const BASE = '/api/onboarding/settings'

/**
 * The headline of the page companies apply from. It is one for the whole
 * installation, because that page opens before any code is typed and so cannot
 * know which session it is for. Empty goes back to the built-in text.
 */
export default function LandingSettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  // Saving over a headline that could not be read would erase it.
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setLoaded(false)
    api
      .get<{ data: OnboardingLandingSettings }>(BASE)
      .then((response) => {
        setTitle(response.data.landing_title ?? '')
        setLoaded(true)
      })
      .catch((err) => setError(toErrorMessage(err) ?? t('errors.generic')))
  }, [open, t])

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.put(BASE, { landing_title: title.trim() || null })
      onOpenChange(false)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('onboarding.landingTitle')}</DialogTitle>
          <DialogDescription>{t('onboarding.landingDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="mt-4 space-y-4">
          <Field label={t('onboarding.landingField')} hint={t('onboarding.landingHint')}>
            <Input
              value={title}
              disabled={!loaded}
              maxLength={ONBOARDING_LIMITS.maxLandingTitleLength}
              placeholder={t('onboardingApply.title')}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          {error && <Alert>{error}</Alert>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!loaded}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
